/**
 * wizardLogic — the pure pieces of the Start-outreach wizard (StartOutreachListModal,
 * docs/crm-outreach-lists-plan.md §B3) that are not already in outreachLogic.ts: the default list
 * name, the study picker's grouping, the summary-bar sentences, the start-time choices, the
 * per-step "why can't I go on" sentences and the create request itself.
 *
 * Same rules as outreachLogic.ts: React-free and axios-free (only `import type` from anything that
 * touches the api client), so the suite runs in Branch Tests' pure-logic step with no mocks. The
 * heavy lifting — the exclusion toggle, the split, the text blocks, the send-time walk, the message
 * rules — stays in outreachLogic and is called from here, never re-derived.
 */
import type { StudyOption, TeamMember } from '../types';
import {
  EXCLUSION_HINTS,
  EXCLUSION_LABELS,
  PRIOR_STAGE_LABELS,
  TEXT_BLOCK_LABELS,
  candidateTextBlocks,
  shortWhen,
  splitRoundRobin,
  type MessageLint,
  type WizardSummary,
} from './outreachLogic';
import type {
  OutreachCandidate,
  OutreachCreateRequest,
  OutreachExclusion,
  OutreachMessageMode,
  OutreachSettings,
  OutreachStudyReadiness,
  OutreachTextBlock,
} from './types';

// ---------------------------------------------------------------------------------------------
// Fixed numbers the wizard states in words — sent explicitly with the create request so what the
// Review step promises is exactly what the list is stored with (plan §B2.1 defaults).
// ---------------------------------------------------------------------------------------------

export const LIST_NAME_MAX = 120;
export const MAX_START_DAYS = 14;
export const PACE_MIN = 1;
export const PACE_MAX = 600;
export const RECENT_DAY_CHOICES = [3, 7, 14, 30] as const;
export const DEFAULT_RECENT_DAYS = 7;
export const TOMORROW_HOUR = 9;
/** The published "new study" opener — it names no study (plan D8). */
export const AGENT_OPENER_KEY = 'texting_agent.template.reengage_aged';

export const FIXED_LIST_SETTINGS = {
  retry_after_hours: 24,
  max_attempts: 3,
  call_after_text_hours: 3,
  agent_call_delay_days: 3,
} as const;

/** The four fields a list text may use (the server refuses anything else). */
export const MESSAGE_VARIABLES: Array<{ token: string; label: string; description: string }> = [
  { token: 'first_name', label: 'First name', description: "The person's first name." },
  { token: 'last_name', label: 'Last name', description: "The person's last name." },
  { token: 'full_name', label: 'Full name', description: 'First and last name together.' },
  { token: 'study_title', label: 'Study title', description: 'The public study title — the name patients see.' },
];

// ---------------------------------------------------------------------------------------------
// Names and small phrases
// ---------------------------------------------------------------------------------------------

/** "<search name> — <study public title>", held to the 120-character column limit. */
export function defaultListName(searchName: string | null | undefined, studyTitle: string | null | undefined): string {
  const base = (searchName || '').trim() || 'Search results';
  const study = (studyTitle || '').trim();
  const full = study ? `${base} — ${study}` : base;
  return full.length <= LIST_NAME_MAX ? full : full.slice(0, LIST_NAME_MAX).trimEnd();
}

/** "Sarah" · "Sarah and Mike" · "Sarah, Mike and Jo". */
export function joinNames(names: string[]): string {
  const n = names.filter(Boolean);
  if (n.length <= 1) return n[0] || '';
  return `${n.slice(0, -1).join(', ')} and ${n[n.length - 1]}`;
}

const plural = (n: number, one: string, many: string) => `${n.toLocaleString()} ${n === 1 ? one : many}`;

export function peoplePhrase(n: number): string {
  return plural(n, 'person', 'people');
}

/** How the list divides, for the summary bar: "106 each for Sarah and Mike". */
export function splitPhrase(split: Record<string, number>, recruiterIds: string[], nameOf: (id: string) => string): string {
  if (recruiterIds.length === 0) return 'no recruiters chosen yet';
  if (recruiterIds.length === 1) return `all for ${nameOf(recruiterIds[0])}`;
  const counts = recruiterIds.map((id) => split[id] ?? 0);
  if (counts.every((c) => c === counts[0])) {
    return `${counts[0].toLocaleString()} each for ${joinNames(recruiterIds.map(nameOf))}`;
  }
  return recruiterIds.map((id) => `${(split[id] ?? 0).toLocaleString()} for ${nameOf(id)}`).join(', ');
}

/** The Recruiters step's live line: "Sarah 106 · Mike 106". */
export function splitDetail(split: Record<string, number>, recruiterIds: string[], nameOf: (id: string) => string): string {
  return recruiterIds.map((id) => `${nameOf(id)} ${(split[id] ?? 0).toLocaleString()}`).join(' · ');
}

/**
 * Who changes hands at launch. Launch always makes the list recruiter the owner (plan D2); the
 * summary's single ownerChanges number mixes "belongs to someone else" with "has no owner", which
 * read differently to an admin — so the Recruiters step splits them, over the same round-robin the
 * server uses.
 */
export function ownershipChanges(included: OutreachCandidate[], recruiterIds: string[]): { fromOthers: number; unowned: number } {
  const split = splitRoundRobin(included.map((c) => c.contact_id), recruiterIds);
  const assignee = new Map<string, string>();
  Object.entries(split).forEach(([r, ids]) => ids.forEach((id) => assignee.set(id, r)));
  let fromOthers = 0;
  let unowned = 0;
  for (const c of included) {
    const to = assignee.get(c.contact_id);
    if (!to) continue;
    if (!c.owner_user_id) unowned += 1;
    else if (c.owner_user_id !== to) fromOthers += 1;
  }
  return { fromOthers, unowned };
}

/** "Not eligible 140 · Unreachable 38 · Not interested 21 · archived 9" — biggest first. */
export function reopenedPhrase(byPrior: Record<string, number>): string {
  return Object.entries(byPrior)
    .filter(([, n]) => n > 0)
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([k, n]) => `${k === 'archived' ? 'archived' : PRIOR_STAGE_LABELS[k] || 'an earlier stage'} ${n.toLocaleString()}`)
    .join(' · ');
}

// ---------------------------------------------------------------------------------------------
// Exclusion + not-texted breakdowns
// ---------------------------------------------------------------------------------------------

/** Table order of plan §B3 (first hit wins), so the breakdown reads in the order rules apply. */
const EXCLUSION_ORDER: OutreachExclusion[] = [
  'enrolled_or_scheduled', 'already_on_study', 'busy_other_study', 'in_agent_conversation',
  'asked_to_stop', 'wrong_number', 'opted_out_sms', 'no_phone', 'on_other_list', 'recently_contacted',
];

export function exclusionRows(excluded: Partial<Record<OutreachExclusion, number>>): Array<{ code: OutreachExclusion; label: string; count: number; hint: string | null }> {
  return EXCLUSION_ORDER
    .filter((code) => (excluded[code] ?? 0) > 0)
    .map((code) => ({ code, label: EXCLUSION_LABELS[code], count: excluded[code] as number, hint: EXCLUSION_HINTS[code] ?? null }));
}

const TEXT_BLOCK_ORDER: OutreachTextBlock[] = ['soft_stopped', 'no_consent_basis', 'too_old', 'prior_not_interested', 'agent_skipped'];

/**
 * The server's own text blocks over the included people, BEFORE the two opt-in toggles lift any —
 * so each checkbox can say how many people it concerns ("Text them too (21)"). Every block a person
 * carries is counted, not just their first: someone both too old and not interested is one of the
 * not-interested people too, even though "text old leads" is the block the server lists first.
 */
export function rawTextBlockCounts(included: OutreachCandidate[]): Partial<Record<OutreachTextBlock, number>> {
  const out: Partial<Record<OutreachTextBlock, number>> = {};
  for (const c of included) {
    for (const b of new Set(candidateTextBlocks(c))) out[b] = (out[b] ?? 0) + 1;
  }
  return out;
}

export function notTextedRows(notTexted: Partial<Record<OutreachTextBlock, number>>): Array<{ code: OutreachTextBlock; label: string; count: number }> {
  return TEXT_BLOCK_ORDER
    .filter((code) => (notTexted[code] ?? 0) > 0)
    .map((code) => ({ code, label: TEXT_BLOCK_LABELS[code], count: notTexted[code] as number }));
}

// ---------------------------------------------------------------------------------------------
// Studies
// ---------------------------------------------------------------------------------------------

export interface WizardStudyGroup { label: string; studies: StudyOption[] }

/**
 * Real CRM studies only (a lead-only slug stub has no id and can't own a list), archived ones
 * hidden, recruiting then paused then the rest, public title A–Z inside each group. `filter`
 * matches either title, the slug or the protocol number.
 */
export function groupStudiesForWizard(studies: StudyOption[], filter = ''): WizardStudyGroup[] {
  const q = filter.trim().toLowerCase();
  const usable = studies.filter((s) => Boolean(s.id) && !s.archived_at && s.source !== 'leads')
    .filter((s) => !q || [s.title, s.internal_title, s.slug, s.protocol_number]
      .some((v) => (v || '').toLowerCase().includes(q)));
  const byTitle = (a: StudyOption, b: StudyOption) => (a.title || a.slug).localeCompare(b.title || b.slug);
  const groups: WizardStudyGroup[] = [
    { label: 'Recruiting', studies: usable.filter((s) => s.status === 'recruiting').sort(byTitle) },
    { label: 'Paused', studies: usable.filter((s) => s.status === 'paused').sort(byTitle) },
    { label: 'Not recruiting', studies: usable.filter((s) => s.status !== 'recruiting' && s.status !== 'paused').sort(byTitle) },
  ];
  return groups.filter((g) => g.studies.length > 0);
}

// ---------------------------------------------------------------------------------------------
// Recruiters
// ---------------------------------------------------------------------------------------------

/** "Out today" / "Out until Mon" / "Out until Oct 12" — null when they're in. `out_until` is the
 *  inclusive last day away (YYYY-MM-DD, GET /team). */
export function outUntilLabel(m: Pick<TeamMember, 'out_today' | 'out_until'>, now: Date = new Date()): string | null {
  if (!m.out_today) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(m.out_until || '');
  if (!match) return 'Out today';
  const last = new Date(+match[1], +match[2] - 1, +match[3]);
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const days = Math.round((last.getTime() - today.getTime()) / 86400000);
  if (days <= 0) return 'Out today';
  if (days <= 6) return `Out until ${last.toLocaleDateString(undefined, { weekday: 'short' })}`;
  return `Out until ${last.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`;
}

// ---------------------------------------------------------------------------------------------
// Start time
// ---------------------------------------------------------------------------------------------

export type StartChoice = 'now' | 'tomorrow' | 'pick';

function wallClock(d: Date, tz: string): { y: number; mo: number; da: number; h: number; mi: number } {
  const f = new Intl.DateTimeFormat('en-US', {
    timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
  });
  const p: Record<string, string> = {};
  f.formatToParts(d).forEach((x) => { p[x.type] = x.value; });
  return { y: +p.year, mo: +p.month, da: +p.day, h: +p.hour, mi: +p.minute };
}

/**
 * `hour`:00 tomorrow on the TENANT's calendar (the send window is tenant-local, so "Tomorrow 9:00"
 * means 9:00 where the patients are). Two-pass offset correction, DST-safe; an unknown zone falls
 * back to the browser's own clock rather than throwing.
 */
export function tomorrowAt(now: Date, hour: number, tz: string): Date {
  try {
    const p = wallClock(now, tz);
    const day = new Date(Date.UTC(p.y, p.mo - 1, p.da + 1));
    const target = Date.UTC(day.getUTCFullYear(), day.getUTCMonth(), day.getUTCDate(), hour, 0, 0);
    let guess = target;
    for (let i = 0; i < 2; i += 1) {
      const w = wallClock(new Date(guess), tz);
      guess += target - Date.UTC(w.y, w.mo - 1, w.da, w.h, w.mi, 0);
    }
    return new Date(guess);
  } catch {
    const d = new Date(now);
    d.setDate(d.getDate() + 1);
    d.setHours(hour, 0, 0, 0);
    return d;
  }
}

/** Value for an `<input type="datetime-local">` (browser-local, minute-floored). */
export function localInputValue(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** The chosen start as an instant; null when "pick a time" has nothing valid in it. */
export function resolveStart(choice: StartChoice, picked: string, now: Date, tz: string): Date | null {
  if (choice === 'now') return now;
  if (choice === 'tomorrow') return tomorrowAt(now, TOMORROW_HOUR, tz);
  if (!picked) return null;
  const d = new Date(picked);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function startProblem(start: Date | null, now: Date): string | null {
  if (!start) return 'Pick when the first message should go out.';
  if (start.getTime() < now.getTime() - 60000) return 'Pick a time that is still to come.';
  if (start.getTime() > now.getTime() + MAX_START_DAYS * 86400000) return `Pick a time within the next ${MAX_START_DAYS} days.`;
  return null;
}

// ---------------------------------------------------------------------------------------------
// The texting agent
// ---------------------------------------------------------------------------------------------

const AGENT_REASONS: Record<string, string> = {
  agent_disabled: 'The texting agent is switched off.',
  study_not_enabled: "The texting agent isn't switched on for this study.",
  study_not_ready: "This study isn't set up for the texting agent yet.",
  study_wrong_network: "The texting agent isn't available for this study.",
  invalid_opener_key: "The agent's opening text for a new study hasn't been published yet.",
  opener_not_published: "The agent's opening text for a new study hasn't been published yet.",
  opener_unknown_merge_fields: "The agent's opening text for a new study has a blank it can't fill in.",
  too_many_active_batches: 'The agent is already running as many batches as it can. Try again when one finishes.',
  no_free_batch_slots: 'The agent is already running as many batches as it can. Try again when one finishes.',
  too_many_targets: 'This list is too big for the agent to take on in one go.',
  no_targets: 'Nobody on this list can be texted by the agent.',
  no_eligible_targets: 'Nobody on this list can be texted by the agent.',
};

/** A plain sentence for why the agent can't take this list. A server-written sentence passes
 *  through; an unknown code never reaches the screen. */
export function agentReasonSentence(code: string | null | undefined): string {
  if (code && AGENT_REASONS[code]) return AGENT_REASONS[code];
  if (code && /\s/.test(code.trim())) return code.trim();
  return "The texting agent isn't available for this study right now.";
}

/** Agent batches hold at most `max_targets_per_batch` people and at most `max_active` run at once. */
export function agentBatchPlan(textPlanned: number, agent: OutreachStudyReadiness['agent']): { batches: number; perBatch: number; freeSlots: number; fits: boolean } {
  const perBatch = Math.max(1, agent.max_targets_per_batch || 250);
  const batches = textPlanned > 0 ? Math.ceil(textPlanned / perBatch) : 0;
  const freeSlots = Math.max(0, (agent.max_active || 0) - (agent.live_batches || 0));
  return { batches, perBatch, freeSlots, fits: batches > 0 && batches <= freeSlots };
}

// ---------------------------------------------------------------------------------------------
// Summary bar
// ---------------------------------------------------------------------------------------------

/**
 * "184 texts start Tue 10:00, last one Tue 5:40 PM". `times` = planSendTimes(count, …); it is empty
 * while the start or pace is still being typed, and then the count is said without the times —
 * never "nobody gets a text" for a list that will get 184.
 */
export function textsPhrase(count: number, times: Date[], fmt: (iso: string) => string = (iso) => shortWhen(iso)): string {
  if (count <= 0) return 'nobody gets a text';
  if (times.length === 0) return `${plural(count, 'text', 'texts')} to send`;
  const first = fmt(times[0].toISOString());
  if (count === 1) return `1 text goes out ${first}`;
  return `${count.toLocaleString()} texts start ${first}, last one ${fmt(times[times.length - 1].toISOString())}`;
}

export function agentPhrase(count: number, times: Date[], fmt: (iso: string) => string = (iso) => shortWhen(iso)): string {
  if (count <= 0) return 'the agent texts nobody';
  if (times.length === 0) return `the agent texts ${count.toLocaleString()}`;
  return `the agent texts ${count.toLocaleString()}, starting ${fmt(times[0].toISOString())}`;
}

export function wizardSummaryParts(i: {
  summary: WizardSummary;
  recruiterIds: string[];
  nameOf: (id: string) => string;
  mode: OutreachMessageMode;
  sendTimes: Date[];
  fmt?: (iso: string) => string;
}): { people: string; split: string; message: string } {
  const n = i.summary.included.length;
  let message = 'no first message';
  if (i.mode === 'text') message = textsPhrase(i.summary.textPlanned, i.sendTimes, i.fmt);
  if (i.mode === 'agent') message = agentPhrase(i.summary.textPlanned, i.sendTimes, i.fmt);
  return { people: peoplePhrase(n), split: splitPhrase(i.summary.split, i.recruiterIds, i.nameOf), message };
}

// ---------------------------------------------------------------------------------------------
// Step checks + the request
// ---------------------------------------------------------------------------------------------

export type WizardStep = 1 | 2 | 3 | 4;

export interface WizardCheckInput {
  name: string;
  studyId: string;
  preflightReady: boolean;
  preflightLoading: boolean;
  summary: WizardSummary | null;
  maxMembers: number;
  recruiterIds: string[];
  mode: OutreachMessageMode;
  lint: MessageLint;
  studyTitleAck: boolean;
  start: Date | null;
  now: Date;
  pace: number;
  agent: OutreachStudyReadiness['agent'] | null;
}

/** Every reason the given step can't be left yet, as sentences (empty = good to go). */
export function stepProblems(step: WizardStep, i: WizardCheckInput): string[] {
  if (step === 4) return [1, 2, 3].flatMap((s) => stepProblems(s as WizardStep, i));
  const out: string[] = [];
  if (step === 1) {
    if (!i.name.trim()) out.push('Give the list a name.');
    else if (i.name.trim().length > LIST_NAME_MAX) out.push(`Keep the name to ${LIST_NAME_MAX} characters or fewer.`);
    if (!i.studyId) out.push('Pick the study these people are being contacted about.');
    else if (i.preflightLoading) out.push('Still checking who can be added…');
    else if (!i.preflightReady || !i.summary) out.push("We couldn't check who can be added yet.");
    else if (i.summary.included.length === 0) out.push('Nobody from this search can be added for this study.');
    else if (i.summary.included.length > i.maxMembers) {
      out.push(`That's ${i.summary.included.length.toLocaleString()} people — a list can hold up to ${i.maxMembers.toLocaleString()}. Narrow the search first.`);
    }
    return out;
  }
  if (step === 2) {
    if (i.recruiterIds.length === 0) out.push('Choose at least one recruiter.');
    return out;
  }
  // Step 3 — the first message.
  if (i.mode === 'none') return out;
  const planned = i.summary?.textPlanned ?? 0;
  if (i.mode === 'text') {
    out.push(...i.lint.errors);
    if (i.lint.namesStudy && !i.studyTitleAck) out.push('Tick the box to confirm this text may name the study.');
    if (i.summary && planned === 0) out.push('Nobody on this list can get a text — choose No message instead.');
  }
  if (i.mode === 'agent') {
    if (!i.agent || !i.agent.available) out.push(agentReasonSentence(i.agent?.reason_code));
    else if (i.summary && planned === 0) out.push('Nobody on this list can be texted by the agent — choose No message instead.');
    else if (i.summary) {
      const plan = agentBatchPlan(planned, i.agent);
      if (!plan.fits) {
        out.push(`This list needs ${plural(plan.batches, 'agent batch', 'agent batches')} but only ${plan.freeSlots.toLocaleString()} ${plan.freeSlots === 1 ? 'is' : 'are'} free. Narrow the list, or wait for a batch to finish.`);
      }
    }
  }
  const sp = startProblem(i.start, i.now);
  if (sp) out.push(sp);
  if (!Number.isFinite(i.pace) || i.pace < PACE_MIN || i.pace > PACE_MAX) {
    out.push(`Set a pace between ${PACE_MIN} and ${PACE_MAX} an hour.`);
  }
  return out;
}

export interface WizardRequestInput {
  name: string;
  runId: string;
  studyId: string;
  recruiterIds: string[];
  mode: OutreachMessageMode;
  body: string;
  templateId: string | null;
  saveAsTemplate: boolean;
  recentDays: number | null;
  includeNotInterestedTexts: boolean;
  textOldLeadsOverride: boolean;
  namesStudy: boolean;
  studyTitleAck: boolean;
  start: Date;
  pace: number;
}

/** POST /outreach-lists body. The server re-runs every check and never trusts these numbers. */
export function buildCreateRequest(i: WizardRequestInput): OutreachCreateRequest {
  const texting = i.mode === 'text';
  const settings: Partial<OutreachSettings> = {
    ...FIXED_LIST_SETTINGS,
    skip_recently_contacted_days: i.recentDays,
    include_not_interested_texts: i.mode !== 'none' && i.includeNotInterestedTexts,
    text_old_leads_override: i.mode !== 'none' && i.textOldLeadsOverride,
    study_title_ack: texting && i.namesStudy && i.studyTitleAck,
  };
  if (i.mode !== 'none') {
    settings.start_at = i.start.toISOString();
    settings.pace_per_hour = Math.round(i.pace);
  }
  if (i.mode === 'agent') settings.agent_opener_key = AGENT_OPENER_KEY;
  return {
    name: i.name.trim(),
    search_run_id: i.runId,
    study_id: i.studyId,
    recruiter_ids: [...i.recruiterIds],
    message_mode: i.mode,
    message_body: texting ? i.body : null,
    template_id: texting ? i.templateId : null,
    save_as_template: texting && !i.templateId && i.saveAsTemplate,
    settings,
  };
}
